/**
 * Logique de l'écran « Barème de TVA ». Les lignes par défaut sont en lecture seule ; l'entreprise ajoute
 * des changements de taux DATÉS. Un changement ne touche jamais une facture déjà émise (taux figé sur la ligne).
 */
import {
  BAREME_TVA_PAR_DEFAUT,
  BaremeTvaInvalideError,
  resoudreBaremeTva,
  type CategorieTva,
  type StatutTauxTva,
  type TauxTvaDate,
} from '@domain/fiscalite/BaremeTva';

export const LIBELLE_CATEGORIE_TVA: Readonly<Record<CategorieTva, string>> = {
  normal: 'Normal',
  intermediaire: 'Intermédiaire',
  reduit: 'Réduit',
  exonere: 'Exonéré',
};

export const LIBELLE_STATUT_TVA: Readonly<Record<StatutTauxTva, string>> = {
  valide: 'valide',
  a_confirmer: 'à confirmer',
};

export const ORDRE_CATEGORIES_TVA: readonly CategorieTva[] = ['normal', 'intermediaire', 'reduit', 'exonere'];

export const AIDE_BAREME_TVA =
  'Un changement de taux ne modifie jamais les factures déjà émises : leur taux reste figé sur chaque ligne de facture. Le nouveau taux est proposé uniquement pour les factures dont la date est égale ou postérieure à la date d’effet.';

/** Écriture du taux en pourcentage ("18", "9,5") vers une fraction (0,18 ; 0,095), arrondie pour éviter les décimales parasites. */
export function fractionDepuisPourcentage(saisie: string): number | null {
  const propre = saisie.replace('%', '').replace(',', '.').trim();
  if (propre === '' || !/^\d+(\.\d+)?$/.test(propre)) return null;
  const pct = Number(propre);
  if (!Number.isFinite(pct) || pct < 0 || pct > 100) return null;
  return Math.round(pct * 100) / 10000;
}

export function pourcentageDepuisFraction(taux: number): string {
  return String(Math.round(taux * 10000) / 100).replace('.', ',');
}

export interface LigneEcranBaremeTva {
  readonly categorie: CategorieTva;
  readonly categorieLibelle: string;
  readonly taux: number;
  readonly tauxPourcent: string;
  readonly depuis: string;
  readonly statut: StatutTauxTva;
  readonly statutLibelle: string;
  readonly source: string;
  readonly parDefaut: boolean;
  readonly supprimable: boolean;
}

function memeLigne(a: TauxTvaDate, b: TauxTvaDate): boolean {
  return (
    a.categorie === b.categorie &&
    a.depuis === b.depuis &&
    a.taux === b.taux &&
    a.statut === b.statut &&
    a.source === b.source
  );
}

function estParDefaut(l: TauxTvaDate): boolean {
  return BAREME_TVA_PAR_DEFAUT.some((d) => memeLigne(d, l));
}

export function construireLignesBareme(bareme: readonly TauxTvaDate[]): LigneEcranBaremeTva[] {
  const ordre = (c: CategorieTva): number => ORDRE_CATEGORIES_TVA.indexOf(c);
  return [...bareme]
    .sort((a, b) => (a.categorie === b.categorie ? a.depuis.localeCompare(b.depuis) : ordre(a.categorie) - ordre(b.categorie)))
    .map((l) => {
      const parDefaut = estParDefaut(l);
      return {
        categorie: l.categorie,
        categorieLibelle: LIBELLE_CATEGORIE_TVA[l.categorie],
        taux: l.taux,
        tauxPourcent: pourcentageDepuisFraction(l.taux),
        depuis: l.depuis,
        statut: l.statut,
        statutLibelle: LIBELLE_STATUT_TVA[l.statut],
        source: l.source,
        parDefaut,
        supprimable: !parDefaut,
      };
    });
}

/** Lignes ajoutées par l'entreprise = barème résolu moins les lignes par défaut. */
export function extraireAjouts(bareme: readonly TauxTvaDate[]): TauxTvaDate[] {
  return bareme.filter((l) => !estParDefaut(l));
}

export function retirerAjout(ajouts: readonly TauxTvaDate[], categorie: CategorieTva, depuis: string): TauxTvaDate[] {
  return ajouts.filter((l) => !(l.categorie === categorie && l.depuis === depuis));
}

/** Texte d'attente de l'expert, affiché seulement si une ligne porte encore le statut « à confirmer ». */
export function texteAttenteExpert(bareme: readonly TauxTvaDate[]): string | null {
  const enAttente = ORDRE_CATEGORIES_TVA.filter(
    (c) => c !== 'normal' && c !== 'exonere' && bareme.some((l) => l.categorie === c && l.statut === 'a_confirmer'),
  );
  if (enAttente.length === 0) return null;
  if (enAttente.includes('reduit')) return 'Le taux réduit est en attente de décision de l’expert-comptable.';
  return 'Le taux intermédiaire est en attente de décision de l’expert-comptable.';
}

export interface SaisieChangementTaux {
  categorie: CategorieTva;
  tauxPourcent: string;
  depuis: string;
  commentaire: string;
}

export type ResultatAjoutTaux =
  | { ok: true; ajouts: TauxTvaDate[] }
  | { ok: false; erreurs: Partial<Record<'categorie' | 'taux' | 'depuis' | 'general', string>> };

export function ajouterChangementDeTaux(
  ajoutsActuels: readonly TauxTvaDate[],
  saisie: SaisieChangementTaux,
): ResultatAjoutTaux {
  const erreurs: Partial<Record<'categorie' | 'taux' | 'depuis' | 'general', string>> = {};
  if (!ORDRE_CATEGORIES_TVA.includes(saisie.categorie)) erreurs.categorie = 'Choisissez une catégorie de TVA.';
  const taux = fractionDepuisPourcentage(saisie.tauxPourcent);
  if (taux === null) erreurs.taux = 'Saisissez un taux entre 0 et 100 (exemple : 18 ou 9,5).';
  const depuis = saisie.depuis.trim();
  if (!/^\d{4}-\d{2}-\d{2}$/.test(depuis) || Number.isNaN(Date.parse(depuis))) {
    erreurs.depuis = 'Indiquez une date d’effet valide.';
  }
  if (Object.keys(erreurs).length > 0 || taux === null) return { ok: false, erreurs };

  const existant = resoudreBaremeTva(ajoutsActuels);
  if (existant.some((l) => l.categorie === saisie.categorie && l.depuis === depuis)) {
    return { ok: false, erreurs: { depuis: 'Un taux existe déjà à cette date pour cette catégorie. Choisissez une autre date d’effet.' } };
  }
  const commentaire = saisie.commentaire.trim();
  const ligne: TauxTvaDate = {
    categorie: saisie.categorie,
    taux,
    depuis,
    statut: 'valide',
    source: commentaire !== '' ? commentaire : 'Changement de taux ajouté par l’entreprise.',
  };
  const ajouts = [...ajoutsActuels, ligne];
  try {
    resoudreBaremeTva(ajouts);
  } catch (err) {
    if (!(err instanceof BaremeTvaInvalideError)) throw err;
    return { ok: false, erreurs: { general: err.message } };
  }
  return { ok: true, ajouts };
}
